import React, { useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import Header from "../../../components/Header/Header";
import Tabs from "../../../components/Tabs/Tabs";
import mes_tab_data from "../../../assets/mockedData/mes_tab_data";
import { savePathToLocalStorage } from "../../../utils/helpers";
import mesApi from "../../../Api/mes.api";
import { BatchListItem } from "../../../types/mes";
import { ToastContainer, toast } from "react-toastify";
import { batchStatusLabel, formatHrDate, formatNum } from "./batchUtils";
import { t } from "../../../i18n/locale";

const isoDaysAgo = (days: number) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().split("T")[0];
};
const isoToday = () => new Date().toISOString().split("T")[0];

const Batches = () => {
  savePathToLocalStorage();
  const history = useHistory();

  const [fromDate, setFromDate] = useState<string>(isoDaysAgo(30));
  const [toDate, setToDate] = useState<string>(isoToday());
  const [search, setSearch] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [rows, setRows] = useState<BatchListItem[]>([]);
  const [loading, setLoading] = useState(false);

  const loadBatches = async () => {
    try {
      setLoading(true);
      const data = await mesApi.getBatches({ fromDate, toDate });
      setRows(data || []);
    } catch (error: any) {
      toast.error(t("Greška pri učitavanju batcheva: {message}", { message: error.message }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusOptions = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => r.BatchStatus && set.add(r.BatchStatus));
    return Array.from(set);
  }, [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFilter && r.BatchStatus !== statusFilter) return false;
      if (!q) return true;
      return (
        r.MixCode?.toLowerCase().includes(q) ||
        (r.ProductName || "").toLowerCase().includes(q) ||
        (r.RecipeCode || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, statusFilter]);

  return (
    <div className="mes-page">
      <Header title={t("MES - Batchevi")} />
      <div className="main-content-flex">
        <Tabs data={mes_tab_data} />
        <div className="section mes-content">
          <ToastContainer position="top-center" autoClose={2000} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
            <h2>{t("Batchevi (zamjesi)")}</h2>
            <span style={{ color: "#999", fontSize: "0.9rem" }}>{t("{count} rezultata", { count: filtered.length })}</span>
          </div>

          <div
            className="mes-section"
            style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "flex-end", marginBottom: "16px" }}
          >
            <div className="form-group" style={{ margin: 0 }}>
              <label>{t("Od datuma")}</label>
              <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label>{t("Do datuma")}</label>
              <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </div>
            <button className="btn-secondary" onClick={loadBatches} disabled={loading}>
              {loading ? t("Učitavanje…") : t("Učitaj")}
            </button>
            <div className="form-group" style={{ margin: 0, flex: 1, minWidth: "220px" }}>
              <label>{t("Pretraga (kod / proizvod / recept)")}</label>
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t("npr. MixCode, naziv proizvoda…")}
              />
            </div>
            <div className="form-group" style={{ margin: 0, minWidth: "160px" }}>
              <label>{t("Status")}</label>
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">{t("Svi")}</option>
                {statusOptions.map((s) => (
                  <option key={s} value={s}>
                    {batchStatusLabel(s)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mes-section">
            {loading ? (
              <p style={{ textAlign: "center", padding: "20px" }}>{t("Učitavanje…")}</p>
            ) : filtered.length === 0 ? (
              <p style={{ textAlign: "center", padding: "40px", color: "#999", fontStyle: "italic" }}>
                {t("Nema batcheva za odabrane filtere.")}
              </p>
            ) : (
              <div className="mes-table-wrap">
                <table className="mes-table">
                <thead>
                  <tr>
                    <th>{t("Kod (MixCode)")}</th>
                    <th>{t("Proizvod")}</th>
                    <th>{t("Datum")}</th>
                    <th>{t("Recept")}</th>
                    <th>{t("Mikser")}</th>
                    <th>{t("Peć")}</th>
                    <th style={{ textAlign: "right" }}>{t("Masa (kg)")}</th>
                    <th>{t("Status")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr
                      key={r.ID}
                      style={{ cursor: "pointer" }}
                      onClick={() => history.push(`/mes/batches/${r.ID}`)}
                    >
                      <td>
                        <strong>{r.MixCode}</strong>
                      </td>
                      <td>{r.ProductName || "-"}</td>
                      <td>{formatHrDate(r.Timeline_TimelineToTimelineBatch?.ProductionDate)}</td>
                      <td>{r.RecipeCode || "-"}</td>
                      <td>{r.MixerName || "-"}</td>
                      <td>{r.OvenName || "-"}</td>
                      <td style={{ textAlign: "right", fontFamily: "monospace" }}>{formatNum(r.FinalMassKg)}</td>
                      <td>
                        <span className={`status-pill status-${r.BatchStatus}`}>{batchStatusLabel(r.BatchStatus)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Batches;
